<template>
  <div>
    <div class="row q-gutter-xs">
      <q-input
        class="col"
        v-show="isSecondLevelTimingParameters"
        :dark="isDarkModeGlobal"
        ref="RepetitionInterval"
        for="RepetitionInterval"
        filled
        dense
        label="Pulse Repetition Interval [ms]"
        type="number"
        step="0.000001"
        :min="repetitionPeriodMinValue"
        v-model="pulse_repetition_interval_ms"
        :rules="[
          val => val > 0 || 'Value must be > 0',
          () => ptd_gteq_pri || 'Pulse Train Duration must be larger than or equal to Pulse Repetition Interval',
          () => ptri_gteq_ptd || 'Pulse Train Repetition Interval must be larger than or equal to Pulse Train Duration',
          () => pri_gteq_pd || 'Pulse Repetition Interval must be larger than or equal to Pulse Duration',
        ]"
        hide-bottom-space
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Total pulse period including ramping, pulse duration and off-time between two pulses; inverse of pulse
                repetition frequency. The repetition interval should always be equal or larger than the pulse duration.
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>
      <q-input
        v-show="isSecondLevelTimingParameters"
        class="col"
        :dark="isDarkModeGlobal"
        ref="RepetitionFrequency"
        for="RepetitionFrequency"
        filled
        dense
        label="Pulse Repetition Frequency [Hz]"
        min="0"
        type="number"
        step="0.000001"
        v-model="pulse_repetition_frequency_Hz"
        :rules="[
          val => val > 0 || 'Value must be > 0',
          () => ptd_gteq_pri || 'Pulse Train Duration must be larger than or equal to Pulse Repetition Interval',
          () => ptri_gteq_ptd || 'Pulse Train Repetition Interval must be larger than or equal to Pulse Train Duration',
          () => pri_gteq_pd || 'Pulse Repetition Interval must be larger than or equal to Pulse Duration',
        ]"
        @update:modelValue="PulseReptitionIntervalUpdateByRepetitionFrequency"
        hide-bottom-space
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Pulse Repetition Frequency = 1 / Pulse Repetition Interval. Total pulse period including ramping, pulse
                duration and off-time between two pulses; inverse of pulse repetition interval. The repetition interval should always be equal or larger than the pulse duration.
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>
    </div>
  </div>
  <div>
    <div class="row q-gutter-xs">
      <q-input
        class="col"
        :dark="isDarkModeGlobal"
        ref="PTD"
        for="PulseTrainDuration"
        v-show="isSecondLevelTimingParameters"
        filled
        dense
        label="Pulse Train Duration [ms]"
        type="number"
        step="0.000001"
        :min="pulse_repetition_interval_ms"
        v-model="pulse_train_duration_ms"
        :rules="[
          val => val > 0 || 'Value must be > 0',
          () => ptd_gteq_pri || 'Pulse Train Duration must be larger than or equal to Pulse Repetition Interval',
          () => ptri_gteq_ptd || 'Pulse Train Repetition Interval must be larger than or equal to Pulse Train Duration',
          () => pri_gteq_pd || 'Pulse Repetition Interval must be larger than or equal to Pulse Duration',
        ]"
        hide-bottom-space
        :hint="pulse_train_duration_ms_hint"
        :hide-hint="pulse_train_duration_ms_hint === ''"
        @blur="clearHint"
        @update:modelValue="timingGraphRoundDown"
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Duration for which pulses are repeated.
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>
    </div>
  </div>
  <div>
    <div class="row q-gutter-xs">
      <q-input
        class="col"
        :dark="isDarkModeGlobal"
        v-show="isSecondLevelTimingParameters"
        ref="RampDur"
        for="pulse_train_ramp_duration"
        filled
        dense
        label="Pulse Train Ramp Duration [ms]"
        type="number"
        step="0.01"
        :min="0"
        :max="pulse_train_ramp_duration_limit"
        v-model="pulse_train_ramp_duration_ms"
        :rules="[
          () => ptrd_isValid || 'Pulse Train Ramp Duration should always be equal or less than Pulse Train Duration minus the last pulse\'s off signal divided by 2.',
        ]"
        hide-bottom-space
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Time from onset of pulse train to attainment of the maximum pressure within a train. Maximum for each pulse
                train might differ if pulse train repetition ramping is also applied.
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>
      <q-input
        class="col"
        :dark="isDarkModeGlobal"
        v-show="isSecondLevelTimingParameters"
        ref="percentagePTRI"
        for="percentage_of_pulse_train_ramp_width"
        filled
        dense
        label="% of PT Ramp Width"
        type="number"
        step="0.000001"
        :min="0"
        :max="50"
        :rules="[
          (val) => val <= 50 || 'To prevent overlapping up and down ramping, the percentage of ramping is cut off at 50%'
        ]"
        v-model="percentage_of_pulse_train_ramp_width"
        hide-bottom-space
        @blur="UpdatePulseTrainRampDuration"
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Pulse Train Ramp Duration can be calculated based on the percentage of the Pulse Train Ramp Width.
                Pulse Train Ramp Width = PTD - (PRI - PD) = Pulse Train Duration - (Pulse Repetition Interval - Pulse Duration)
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>
    </div>
  </div>
  <q-select
    class="col"
    :dark="isDarkModeGlobal"
    v-show="isSecondLevelTimingParameters"
    filled
    dense
    label="Pulse Train Ramp Shape"
    type="text"
    :options="rampShapes"
    v-model="pulse_train_ramp_shape"
  >
    <template #append>
      <q-btn
        icon="help"
        color="primary"
        size="sm"
        dense
        flat
      >
        <q-tooltip>
          Show note
        </q-tooltip>
        <q-popup-proxy :offset="[-70, 20]">
          <q-banner :dark="isDarkModeGlobal">
            <template #avatar>
              <q-icon
                name="help"
                color="primary"
              />
            </template>
            For example Linear or Tukey.
          </q-banner>
        </q-popup-proxy>
      </q-btn>
    </template>
  </q-select>
  <q-input
    class="col"
    v-show="isSecondLevelTimingParameters"

    :dark="isDarkModeGlobal"
    ref="DutyCycleOfPT"
    for="DutyCycleOfPT"
    outlined
    readonly
    dense
    label="Duty Cycle of PT"
    type="number"
    step="0.000001"
    min="0"
    v-model="calculatorStore.duty_cycle_of_pulse_train"
    hide-bottom-space
  />
</template>
<script setup lang="ts">
import {computed, ref, watch} from "vue"
import {storeToRefs} from "pinia"
import {useCalculatorStore} from '../stores/calculator.store'
import {formatNumber} from "./utils";

const calculatorStore = useCalculatorStore()
const {
  isSecondLevelTimingParameters,
  pulse_duration_ms,
  pulse_repetition_interval_ms,
  pulse_train_duration_ms,
  pulse_train_ramp_duration_ms,
  pulse_train_ramp_shape,
  pulse_train_repetition_interval_ms,
  time_parameters_valid,
  isDarkModeGlobal,
  rampShapes,
  // percentage_of_ptri,
  percentage_of_pulse_train_ramp_width,
  // percentage_of_pri,
  updateSeenTrainSection,
  importingData
} = storeToRefs(calculatorStore)

const PTD = ref(null)
const RampDur = ref(null)
const percentagePTRI = ref(null)
const RepetitionInterval = ref(null)
const RepetitionFrequency = ref(null)
const repetitionPeriodMinValue = computed(() => { return +calculatorStore.pulse_duration_ms })
const pulse_train_duration_ms_hint = computed(() => {
  return oldPulse_train_duration_ms.value != pulse_train_duration_ms.value
  && updateSeenTrainSection.value === false
  && oldPulse_train_duration_ms.value !== undefined
  && roundDown
      ? `This timing parameter has been rounded down to obtain complete pulses and/or pulse trains for smooth ramping
             and realistic US protocols. Pulse train duration was rounded down from ${oldPulse_train_duration_ms.value}
             to ${pulse_train_duration_ms.value}`
      : ''
})

// RULES
// Don't allow a Pulse Train Repetition Interval smaller than Pulse Train Duration (equal is allowed).
const ptri_gteq_ptd = computed(() => +pulse_train_repetition_interval_ms.value >= +pulse_train_duration_ms.value)
// The Pulse Repetition Interval should always be larger than the Pulse Duration.
const pri_gteq_pd = computed(() => +pulse_repetition_interval_ms.value >= +pulse_duration_ms.value)
// Don't allow a Pulse Train Duration smaller than the Pulse Repetition Interval (equal is allowed).
const ptd_gteq_pri = computed(() => +pulse_train_duration_ms.value >= +pulse_repetition_interval_ms.value)
// Don't allow a Pulse Train Repetition Duration smaller than the Pulse Train Repetition Interval (equal is allowed).
// const ptrd_gteq_ptri = computed(() => +pulse_train_repetition_duration_ms.value >= +pulse_train_repetition_interval_ms.value)

const pulse_train_ramp_duration_limit = computed(() =>
    (+pulse_train_duration_ms.value / 2) - (Math.floor(pulse_repetition_interval_ms.value - pulse_duration_ms.value) / 2)
)
const ptrd_isValid = computed(() => +pulse_train_ramp_duration_ms.value <= pulse_train_ramp_duration_limit.value)

const oldPulse_train_duration_ms = ref()
const oldPercentagePTRI = ref()
const division = computed(() => pulse_train_duration_ms.value / pulse_repetition_interval_ms.value)
const roundDown = computed(() => !Number.isInteger(division.value))
const multiplier = computed(() => Math.floor(division.value))
const pulse_repetition_frequency_Hz = ref(formatNumber(1 / (pulse_repetition_interval_ms.value / 1000)))

function PulseReptitionIntervalUpdateByRepetitionFrequency () {
  if (+pulse_repetition_frequency_Hz.value > 0) {
    pulse_repetition_interval_ms.value = formatNumber((1 / +pulse_repetition_frequency_Hz.value) * 1000)
  }
  calculatorStore.showRecalculateButton()
}

const pulseTrainRampWidth = computed(() => {
  return pulse_train_duration_ms.value - (pulse_repetition_interval_ms.value - pulse_duration_ms.value)
})

function UpdatePulseTrainRampDuration() {
  if (percentage_of_pulse_train_ramp_width.value > 50) {
    percentage_of_pulse_train_ramp_width.value = 50
  } else {
    percentagePTRI.value.resetValidation()
  }

  if (!importingData.value) {
    pulse_train_ramp_duration_ms.value = formatNumber((percentage_of_pulse_train_ramp_width.value / 100) * pulseTrainRampWidth.value)
  }
  calculatorStore.showRecalculateButton()
}

function clearHint () {
  updateSeenTrainSection.value = true
  oldPulse_train_duration_ms.value = undefined
}

function timingGraphRoundDown() {
  updateSeenTrainSection.value = false
  oldPulse_train_duration_ms.value = pulse_train_duration_ms.value

  if (roundDown.value) {
    // Calculate % PTRI using
    oldPercentagePTRI.value = formatNumber((pulse_train_duration_ms.value / pulse_train_repetition_interval_ms.value) * 100)

    const pulse_train_duration_rounded_down = (pulse_repetition_interval_ms.value * multiplier.value)
    pulse_train_duration_ms.value = Number.isInteger(pulse_train_duration_rounded_down) ? pulse_train_duration_rounded_down : formatNumber(pulse_train_duration_rounded_down)

  }
}

watch(time_parameters_valid, (bool) => {
  if (bool) {
    PTD.value.resetValidation()
    RampDur.value.resetValidation()
    RepetitionInterval.value.resetValidation()
    RepetitionFrequency.value.resetValidation()
    percentagePTRI.value.resetValidation()
  }
})

// The pulse train duration default to the pulse repetition interval
watch(pulse_repetition_interval_ms, (newVal) => {
  if (+newVal > 0) {
    pulse_repetition_frequency_Hz.value = formatNumber(1 / (+newVal / 1000))
    if (+newVal > pulse_train_duration_ms.value) {
      pulse_train_duration_ms.value = +newVal
    }
    timingGraphRoundDown()
    // percentage_of_pri.value = formatNumber((+pulse_duration_ms.value / +pulse_repetition_interval_ms.value) * 100)
    UpdatePulseTrainRampDuration()
  }
})

// The pulse train repetition interval default to the pulse train duration
watch(pulse_train_duration_ms, (newVal) => {
  if (+newVal > pulse_train_repetition_interval_ms.value || !pulse_train_repetition_interval_ms.value) {
    pulse_train_repetition_interval_ms.value = +newVal
  }
  calculatorStore.showRecalculateButton()
})

watch(pulse_train_ramp_duration_ms, () => {
    if (ptrd_isValid.value) {
      percentage_of_pulse_train_ramp_width.value = formatNumber((pulse_train_ramp_duration_ms.value / pulseTrainRampWidth.value) * 100)
    }
    calculatorStore.showRecalculateButton()
})

watch(pulse_duration_ms, UpdatePulseTrainRampDuration)
watch(pulse_train_ramp_shape, () => calculatorStore.showRecalculateButton())

</script>